import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';

const ShareRide = ({ bookingId, rideId, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleShare = async () => {
    // Reset states
    setError(null);
    setSuccess(false);
    setShareUrl(null);

    // Validate email
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate that we have either bookingId or rideId
    if (!bookingId && !rideId) {
      setError('No active ride found. Please make sure you have an ongoing ride.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('uttaapp_token');
      if (!token) {
        setError('You must be logged in to share a ride');
        setLoading(false);
        return;
      }

      const apiUrl = getApiUrl();
      
      console.log('📤 Sending share ride request:', { 
        bookingId, 
        rideId, 
        email: email.trim() 
      });
      
      const response = await axios.post(
        `${apiUrl}/api/shared-ride/share`,
        {
          email: email.trim(),
          bookingId: bookingId || null,
          rideId: rideId || null
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('📥 Share ride response:', response.data);

      if (response.data.success) {
        setSuccess(true);
        setShareUrl(response.data.shareUrl);
        setEmail('');
        console.log('✅ Ride share link generated:', response.data.shareUrl);
      } else {
        setError(response.data.message || 'Failed to share ride');
      }
    } catch (err) {
      console.error('❌ Error sharing ride:', err);
      const errorMessage = err.response?.data?.message || 
                           err.message || 
                           'Failed to share ride. Please try again.';
      console.error('Error details:', {
        status: err.response?.status,
        message: errorMessage,
        data: err.response?.data
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        // Show success message instead of alert
        const successMsg = document.createElement('div');
        successMsg.textContent = '✅ Link copied to clipboard!';
        successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 2000);
      }).catch(() => {
        // Fallback: select text for manual copy
        const input = document.querySelector('input[readonly]');
        if (input) {
          input.select();
          input.setSelectionRange(0, 99999);
          alert('Please copy the link manually (Ctrl+C or Cmd+C)');
        }
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Share Ride via Gmail</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {!success ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Gmail ID
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={loading || !email.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Share Ride'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600 font-medium mb-2">
                ✅ Ride share link sent successfully!
              </p>
              <p className="text-xs text-green-600">
                An email with the ride share link has been sent to {email}
              </p>
            </div>

            {shareUrl && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareRide;

